/**
 * Asset preloading for the door.
 *
 * Two tiers, and the split is the whole point:
 *
 *   DOOR_SPRITES    block the rope. Nothing is interactive until these decode,
 *                   because the closed door *is* the loading screen.
 *   ARM_SPRITES     must never block anything. Fetched in the background as
 *                   soon as the door is usable, so they are warm long before
 *                   the bribe appears.
 *   BUTTON_SPRITES  same deal, one beat earlier: the slab buttons are on
 *                   screen from the moment the scene mounts.
 *
 * The arm tier exists because of a specific bug: `arm_extender` is painted as
 * a CSS `background-image`, and a background is not fetched until an element
 * that uses it exists. That element only exists once the hand leaves its
 * resting place — so the forearm popped in visibly on the first pull, every
 * time, no matter how long the page had been open.
 */

export const DOOR_SPRITES = [
  '/assets/dizelasi_obojica.png',
  '/assets/dizelasi_Armane.png',
  '/assets/dizelasi_Versace.png',
  '/assets/dizelasi_ne.png',
] as const;

export const ARM_SPRITES = [
  '/assets/arm_base.png',
  '/assets/arm_fingers.png',
  '/assets/1000RSD.png',
  // The one that was arriving late.
  '/assets/arm_extender.png',
] as const;

/**
 * The audition: two titovkas and a microphone.
 *
 * Same tier as the arms — warmed when the door becomes usable, never blocking.
 * The hats are a *layer over* an already-visible face, so an unwarmed one does
 * not pop in unnoticed the way a whole sprite would; it lands on a head that is
 * already on screen, one beat late, and looks like a bug in the animation.
 *
 * The microphone is here for the opposite reason: it is 250px tall and slides
 * in over half a second, which is exactly long enough for a cold fetch to make
 * it arrive halfway up.
 */
export const SING_SPRITES = [
  '/assets/titovka_Armane.png',
  '/assets/titovka_Versace.png',
  '/assets/microphone.png',
] as const;

/**
 * The slab button, both states. Every one of these six is a CSS background, so
 * the same rule that caught `arm_extender` applies twice over here:
 *
 *   - the idle caps and tile are not fetched until a `.slab` exists, and the
 *     first one to exist is TON in the corner of the scene;
 *   - the pressed caps and tile are not fetched until something is `:active`,
 *     i.e. the exact frame of the first tap. Un-warmed, the slab blinks empty
 *     under your finger and un-squishes before the sprite lands.
 *
 * The pressed set is also needed at *mount*, not just on tap: a visitor who
 * muted last time renders TON in `.slab--pressed` on the first paint.
 */
export const BUTTON_SPRITES = [
  '/ui/btn-idle-l.png',
  '/ui/btn-idle-m.png',
  '/ui/btn-idle-r.png',
  '/ui/btn-press-l.png',
  '/ui/btn-press-m.png',
  '/ui/btn-press-r.png',
] as const;

/**
 * Resolves when the image is fetched *and* rasterised, or immediately if it
 * fails. A 404 must never hold anything up — principle 4: the joke is never
 * load-bearing, and that includes its assets.
 */
export function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // decode() moves the rasterisation cost off the first paint that uses
      // the image. Not supported everywhere; onload alone is good enough then.
      if (typeof img.decode === 'function') img.decode().then(() => resolve(), () => resolve());
      else resolve();
    };
    img.onerror = () => resolve();
    img.src = src;
  });
}

export function preloadImages(srcs: readonly string[]): Promise<void[]> {
  return Promise.all(srcs.map(preloadImage));
}

/**
 * Shared shape for the background tiers: latch, then fetch in idle time so it
 * cannot compete with whatever the person is currently waiting on.
 */
function warmer(srcs: readonly string[]): () => void {
  let done = false;
  return () => {
    if (done) return;
    done = true;

    const run = () => void preloadImages(srcs);
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      window.setTimeout(run, 200);
    }
  };
}

/**
 * Idempotent, and safe to call from several places — the door calls it once
 * it is interactive, and the bribe calls it on mount as a backstop for the
 * paths that skip the door scene entirely.
 */
export const warmArmSprites = warmer(ARM_SPRITES);

/** Four taps from `start` down the no-code branch. Same budget as the arms. */
export const warmSingSprites = warmer(SING_SPRITES);

/**
 * Called from the same place, but *before* the arms: the buttons are needed
 * one tap from now and the arms several, and idle callbacks run in order.
 *
 * The door is the only way into the app, so warming here also covers the
 * Kulčina editor's slabs on the home page — by then they are in cache.
 */
export const warmButtonSprites = warmer(BUTTON_SPRITES);
